import { useState } from 'react';

import { type CoreJsonAPIResponse } from '../CoreJsonAPIResponse';
import { type EFactsheetClasses } from '../enums/EFactsheetClasses';
import { ELocalStorageItems } from '../enums/ELocalStorageItems';
import { type IResponse } from '../models/IResponse';
import { type TermsAgreement } from '../models/ITermsAgreement';
import { type TermsOfServiceAgreements } from '../models/TTermsOfServiceAgreements';
import { type ApiResponse } from '../services/baseApiService';
import { type LanguageJson } from '../services/i18nService';

/**
 * Helper function to update an attribute in the component's state.
 * @deprecated Use updateStateAttribute instead.
 * @param obj
 * @param state
 * @param setState
 */
export function updateState<Type>(
  obj: Partial<Type>,
  state: Type,
  setState: (st: (s: Type) => Type & Partial<Type>) => void
) {
  setState((state) => ({ ...state, ...obj }));
}

/**
 * Helper function to update an attribute in the component's state.
 * @param partialState
 * @param setStateCallback
 */
export function updateStateAttribute<T>(
  partialState: Partial<T>,
  setStateCallback: (st: (s: T) => T & Partial<T>) => void
) {
  setStateCallback((s) => ({ ...s, ...partialState }));
}

/**
 * @deprecated Use updateStateResponseStatefulAttribute instead
 * Helper function to update a IResponse<T> attribute in the component's state.
 * @param partialState
 * @param setStateCallback
 * @param requestCallback
 * @param requestCallbackParam
 * Ticket #589
 */
export async function updateStateResponseStatefulAttributeDepr<CallbackParamType, ResponseType, StateType>(
  partialState: Partial<StateType>,
  setStateCallback: (st: (s: StateType) => StateType & Partial<StateType>) => void,
  requestCallback: (param?: CallbackParamType) => Promise<ApiResponse<ResponseType>>,
  requestCallbackParam?: CallbackParamType
) {
  const modifiedAttribute = { ...partialState } as any;
  const objectKey = Object.keys(partialState)[0];
  modifiedAttribute[objectKey] = {
    ...modifiedAttribute[objectKey],
    state: { ...(partialState as any as IResponse<ResponseType>).state, isLoading: true }
  };
  setStateCallback((s) => ({ ...s, ...modifiedAttribute }));
  await requestCallback(requestCallbackParam);
  const res = await requestCallback(requestCallbackParam);
  if (res.hasError()) {
    modifiedAttribute[objectKey] = {
      ...modifiedAttribute[objectKey],
      state: {
        isLoading: false,
        isError: true,
        strError: res.getErrorString(),
        objError: res.getErrorObj()
      }
    };
    setStateCallback((s) => ({
      ...s,
      ...modifiedAttribute
    }));
  } else {
    modifiedAttribute[objectKey] = {
      ...modifiedAttribute[objectKey],
      data: res.getData(),
      state: { isLoading: false, isError: false }
    };
    setStateCallback((s) => ({
      ...s,
      ...modifiedAttribute
    }));
  }
}

/**
 * Helper function to update a IResponse<T> attribute in the component's state.
 * @param partialState
 * @param setStateCallback
 * @param requestCallback
 */
export async function updateStateResponseStatefulAttribute<StateType, ResponseType>(
  partialState: Partial<StateType>,
  setStateCallback: (st: (s: StateType) => StateType & Partial<StateType>) => void,
  requestCallback: () => Promise<ApiResponse<ResponseType> | CoreJsonAPIResponse<ResponseType>>
) {
  const modifiedAttribute = { ...partialState } as any;
  const objectKey = Object.keys(partialState)[0];
  modifiedAttribute[objectKey] = {
    ...modifiedAttribute[objectKey],
    state: { ...(partialState as any as IResponse<ResponseType>).state, isLoading: true }
  };
  setStateCallback((s) => ({ ...s, ...modifiedAttribute }));
  const res = await requestCallback();

  let hasError: boolean;
  let errorString: string | undefined;
  let errorObject: Record<string, string[]> | undefined;

  function isCoreJsonAPIResponse(object: any): boolean {
    return (
      typeof object?.isSuccess === 'function' &&
      typeof object?.getMessage === 'function' &&
      typeof object?.getDetail === 'function'
    );
  }

  if (isCoreJsonAPIResponse(res)) {
    const newRes = res as CoreJsonAPIResponse<ResponseType>;
    hasError = !(await newRes.isSuccess(false));
    errorString = !hasError ? undefined : await newRes.getMessage(false);
    errorObject = await newRes.getDetail(false);
  } else {
    const oldRes = res as ApiResponse<ResponseType>;
    hasError = oldRes.hasError();
    errorString = oldRes.getErrorString();
    errorObject = oldRes.getErrorObj();
  }

  if (hasError) {
    modifiedAttribute[objectKey] = {
      ...modifiedAttribute[objectKey],
      state: {
        isLoading: false,
        isError: true,
        strError: errorString,
        objError: errorObject
      }
    };
  } else if (typeof res.getData() === 'string' && (res.getData() as unknown as string).includes('NaN,')) {
    modifiedAttribute[objectKey] = {
      ...modifiedAttribute[objectKey],
      state: {
        isLoading: false,
        isError: true,
        strError: 'nanInResponse'
      }
    };
  } else {
    modifiedAttribute[objectKey] = {
      ...modifiedAttribute[objectKey],
      data: await res.getData(),
      state: { isLoading: false, isError: false }
    };
  }
  setStateCallback((s) => ({
    ...s,
    ...modifiedAttribute
  }));
}

export function useStateExtended<Type>(initialState: Type) {
  const [state, setState] = useState<Type>(initialState);
  const getLatestState = async () => {
    // Returns the latest state when accessed via a callback
    return await new Promise<Type>((resolve) => {
      setState((s) => {
        resolve(s);
        return s;
      });
    });
  };

  return [state, setState, getLatestState] as const;
}

export function disableScroll(isTrue: boolean): void {
  const scrollbarW = 0;
  if (isTrue) {
    document.body.style.top = `-${window.scrollY}px`;
    document.body.style.position = 'fixed';
    document.body.style.paddingRight = `${scrollbarW}px`;
  } else {
    const scrollY = document.body.style.top;
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo(0, parseInt(scrollY ?? '0') * -1);
    document.body.style.paddingRight = '0px';
  }
}

export function debounce(fn: () => void, dur?: number): () => void {
  const time = dur ?? 0; // 10 by default if no param
  let timer: NodeJS.Timeout;
  return () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(fn, time);
  };
}

export function formatDate(time: number | string | Date, options?: Intl.DateTimeFormatOptions): string {
  let dateObject: Date;

  if (typeof time === 'number' || typeof time === 'string') {
    dateObject = new Date(time);
  } else {
    dateObject = time;
  }

  return dateObject.toLocaleString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'long',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'shortGeneric',
    ...options
  });
}

export function parseJwt(token: string) {
  if (!token || token === '') return undefined;
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join('')
  );

  return JSON.parse(jsonPayload);
}

/**
 * Terms of Service localStorage management utilities
 */

/**
 * Check if terms of service have been accepted and are still valid
 * @param subitemKey - The key for the specific terms (e.g., factsheet-some-id)
 * @returns boolean - true if terms are accepted and not expired, false otherwise
 */
export function areTermsAcceptedAndValid(subitemKey: string): boolean {
  const termsData = localStorage.getItem(ELocalStorageItems.TERMS_OF_SERVICE_AGREEMENTS);

  if (!termsData) {
    return false;
  }

  let agreements: TermsOfServiceAgreements;
  try {
    agreements = JSON.parse(termsData);
  } catch (error) {
    // Invalid JSON data, treat as invalid
    return false;
  }

  // Check if the specific subitem exists
  if (!agreements[subitemKey]) {
    return false;
  }

  const termsAgreement = agreements[subitemKey];

  // Validate that the stored object contains ALL required properties from TermsAgreement interface
  // This ensures older versions with missing properties are deprecated
  if (!isTermsAgreementObjectProperlyFormatted(termsAgreement)) {
    return false;
  }

  // Check if the terms are not expired
  const currentTimestamp = Date.now();
  const expiryTimestamp = termsAgreement.dateOfExpiry;

  return currentTimestamp < expiryTimestamp;
}

/**
 * Validates that a terms agreement object contains all required properties
 * @param termsAgreement - The terms agreement object to validate
 * @returns boolean - true if all required properties are present with correct types
 */
function isTermsAgreementObjectProperlyFormatted(termsAgreement: any): termsAgreement is TermsAgreement {
  // Check if the object exists
  if (!termsAgreement || typeof termsAgreement !== 'object') {
    return false;
  }

  // Validate factsheetId property
  if (typeof termsAgreement.factsheetId !== 'string' || termsAgreement.factsheetId.trim() === '') {
    return false;
  }

  // Validate dateOfExpiry property
  if (
    typeof termsAgreement.dateOfExpiry !== 'number' ||
    isNaN(termsAgreement.dateOfExpiry) ||
    termsAgreement.dateOfExpiry <= 0
  ) {
    return false;
  }

  // If we add more properties to TermsAgreement in the future,
  // add validation for them here to ensure older versions are deprecated

  return true;
}

/**
 * Save terms of service agreement for a specific subitem with 1 year expiration
 * @param subitemKey - The key for the specific terms (e.g., 'factsheetUsAmericanStocks')
 * @param factsheetId - The Factsheet ID for which we want to save the terms acceptance object for.
 */
export function saveTermsAgreement(subitemKey: string, factsheetId: string): void {
  // Clean up old localStorage keys first
  const agreements: TermsOfServiceAgreements = cleanInvalidTermsAgreements();

  // Calculate expiry date (365 days from now)
  const oneYearFromNow = Date.now() + 365 * 24 * 60 * 60 * 1000;

  // Add/update the agreement for this subitem
  agreements[subitemKey] = {
    factsheetId,
    dateOfExpiry: oneYearFromNow
  };

  // Save back to localStorage
  localStorage.setItem(ELocalStorageItems.TERMS_OF_SERVICE_AGREEMENTS, JSON.stringify(agreements));
}

/**
 * Cleans up old localStorage keys
 */
function cleanInvalidTermsAgreements(): TermsOfServiceAgreements {
  // Remove old localStorage keys
  localStorage.removeItem(ELocalStorageItems.FACTSHEET_ID);
  localStorage.removeItem(ELocalStorageItems.FACTSHEET_TERMS_ACCEPTED);

  // Get existing agreements or create new object
  const existingData = localStorage.getItem(ELocalStorageItems.TERMS_OF_SERVICE_AGREEMENTS);
  let agreements: TermsOfServiceAgreements = {};

  if (existingData) {
    try {
      agreements = JSON.parse(existingData);
    } catch (error) {
      // Invalid JSON, start fresh
      agreements = {};
    }
  }
  return agreements;
}

export function getFactsheetId(factsheetClass: EFactsheetClasses): string | undefined {
  try {
    const tos = JSON.parse(localStorage.getItem(ELocalStorageItems.TERMS_OF_SERVICE_AGREEMENTS) ?? '{}');
    return tos?.[factsheetClass]?.factsheetId;
  } catch {
    return undefined;
  }
}

export default function flattenLanguageJson(
  languageJson: LanguageJson,
  locale: string
): Record<string, string> {
  const out: Record<string, string> = {};

  function walk(obj: Record<string, any>, path: string[] = []) {
    for (const [key, value] of Object.entries(obj)) {
      const nextPath = [...path, key];

      if (
        value &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        typeof value[locale] === 'string'
      ) {
        out[nextPath.join('.')] = value[locale];
        continue;
      }

      if (value && typeof value === 'object' && !Array.isArray(value)) {
        walk(value, nextPath);
      }
    }
  }

  walk(languageJson);

  return out;
}

export function modulo(n: number, div: number) {
  return ((n % div) + div) % div;
}
